import { Component, ElementRef, Input, OnInit, QueryList, ViewChildren, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { forkJoin, from, Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { CommonService } from 'src/app/core/services/common.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { EnforcementService } from 'src/app/core/services/enforcement.service';

@Component({
  selector: 'app-enforcement-detail',
  templateUrl: './enforcement-detail.component.html',
  styleUrls: ['./enforcement-detail.component.css']
})
export class EnforcementDetailComponent implements OnInit {

  isArabic!: boolean;
  @Input() enforcementList: any;
  form!: FormGroup;
  enforcementDetail: any[] = [];
  userData: any;
  enforcementId?: string;
  isHistorical?: string;
  @ViewChildren('fileInputPerFile') fileInputs!: QueryList<ElementRef<HTMLInputElement>>;

  constructor(
    private router: Router,
    private storageService: StorageService,
    private commonService: CommonService,
    private fb: FormBuilder,
    private enforcementService: EnforcementService,
    private cd: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.isArabic = this.storageService.getSessionStorageData('isArabic');


    this.form = this.fb.group({
      enforcement: ['']
    });


    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state as { enforcementId?: string, isHistorical?: boolean };

    this.enforcementId = state?.enforcementId ||
      history.state?.enforcementId ||
      this.storageService.getSessionStorageData('enforcementId');

    this.isHistorical = state?.isHistorical ||
      history.state?.isHistorical ||
      this.storageService.getSessionStorageData('isHistorical');

    this.fetchEnforcementDetails();

  }

  fetchEnforcementDetails() {
    // Ensure we have IDs before calling the API
    if (!this.enforcementId) {
      console.warn("Cannot fetch details: enforcementId is missing.");
      return;
    }

    const requestData = {
      enforcementId: this.enforcementId,
      isHistorical: this.isHistorical
    };

    // Determine which service method to use
    const fetchObservable = this.isHistorical
      ? this.enforcementService.fetchEnforcementHistoricalDetail(requestData)
      : this.enforcementService.fetchEnforcementDetail(requestData);

    fetchObservable.subscribe(
      (res) => {
        if (res?.data) {
          const decryptedData = this.commonService.decryptUsingAES256(res?.data);

          if (this.isHistorical) {
            this.enforcementDetail = JSON.parse(JSON.stringify(decryptedData));
          } else if (Array.isArray(decryptedData)) {
            const ACTIVE_STATUS_CODE = 755020000;
            const DRAFT_STATUS_CODE = 755020003;

            const filteredActiveRecords = decryptedData.filter(item =>
              item.mde_enforcementstatus === ACTIVE_STATUS_CODE ||
              item.mde_enforcementstatus === DRAFT_STATUS_CODE
            );
            this.enforcementDetail = JSON.parse(JSON.stringify(filteredActiveRecords));
          }

          this.initializeEnforcementUiState();
        }
      },
      (error: any) => {
        console.error('Error fetching enforcement detail', error);
      }
    );
  }

  // Add small UI state for each row
  initializeEnforcementUiState() {
    this.enforcementDetail = (this.enforcementDetail || []).map((enf: any) => {
      enf.comments = enf.comments || '';
      enf.disableSubmit = false;
      // you may have a status property to decide this — adjust accordingly
      enf.isSubmitted = enf.mde_status === 'Submitted' || enf.isSubmitted || false;
      enf.showError = false;
      enf.fetchedDocuments = []; // Array to store documents for this specific enforcement item
      enf.showDocuments = false;
      return enf;
    });
  }

  // Comment handling
  onEnforcementCommentChange(event: any, idx: number) {
    const value = (event.target && event.target.value) || event;
    this.enforcementDetail[idx].comments = value;
  }

  onEnforcementKeyup(event: any, idx: number) {
    const value = (event.target && event.target.value) || '';
    this.checkEnforcementError(this.enforcementDetail[idx], value);
  }

  onDeleteNewFile(enforcementIndex: number, fileIndex: number) {
    const enforcement = this.enforcementDetail[enforcementIndex];
    if (!enforcement?.attachedFiles || !enforcement?.fileNames) return;

    enforcement.attachedFiles.splice(fileIndex, 1);
    enforcement.fileNames.splice(fileIndex, 1);

    // Trigger change detection with new array references if needed
    enforcement.attachedFiles = [...enforcement.attachedFiles];
    enforcement.fileNames = [...enforcement.fileNames];
  }

  checkEnforcementError(enf: any, value: string) {
    enf.showError = !(value && value.length > 0);
  }

  submitEnforcementCheck(enf: any, idx: number) {
    this.checkEnforcementError(enf, enf?.comments);
    if (!enf.showError) {
      // For minimal change we submit directly; if you want a confirmation dialog, insert it here.
      this.submitEnforcement(enf, idx);

    }
  }

  submitEnforcement(enf: any, idx: number) {
    enf.disableSubmit = true;

    const now = new Date();
    const dateString = `${now.getDate()}.${now.getMonth() + 1}.${now.getFullYear()}`;
    const userName = `${this.userData?.firstname || ''} ${this.userData?.lastname || ''}`.trim();
    const userComment = `${enf.comments}`;

    const conversationHistory = (enf.mde_comment || '') ?
      enf.mde_comment + '\n' + userComment :
      userComment;

    const enforcementRqstBody: any = {
      enforcementId: enf.mde_enforcementid || enf.enforcementId || enf.id,
      comments: enf.comments,
      commentHistory: conversationHistory,
      actualCompletionDate: new Date()
    };

    const save$ = this.enforcementService.saveEnforcement ?
      this.enforcementService.saveEnforcement(enforcementRqstBody) : of({ data: null });

    if (!enf.attachedFiles || enf.attachedFiles.length === 0) {
      // No files to upload; proceed with save only
      save$.subscribe(() => {
        enf.disableSubmit = false;
        this.postSubmitCleanup(enf, conversationHistory);
      }, err => {
        enf.disableSubmit = false;
        console.error(err);
      });
      return;
    }

    this.showUploadPopup();
    this.updateUploadProgress(0);
    const totalFiles = enf.attachedFiles.length;
    let uploadedFiles = 0;
    // Map one Observable per file upload
    const uploadObservables: Observable<any>[] = enf.attachedFiles.map((file: File) => {
      // Convert the Promise<string> returned by fileToBase64 into Observable<string>
      return from(this.commonService.fileToBase64(file) as Promise<string>).pipe(
        switchMap((base64Data: string) => {
          const attachmentRqstBody = {
            subjectId: enf.mde_enforcementid || enf.enforcementId || enf.id,
            filename: file.name,
            responseguid: enf.mde_enforcementid || enf.enforcementId || enf.id,
            documentBody: base64Data,
            notesType: 'enforcement'
          };
          // Return Observable from the upload API call
          return this.enforcementService.uploadFileToServer(attachmentRqstBody);
        }),
        tap(() => {
          uploadedFiles++;
          const percentDone = Math.round((uploadedFiles / totalFiles) * 100);
          this.updateUploadProgress(percentDone);
        })
      );
    });

    // Run save and uploads in parallel, wait for all to complete
    forkJoin([save$, forkJoin(uploadObservables)]).subscribe(() => {
      enf.disableSubmit = false;
      this.postSubmitCleanup(enf, conversationHistory);
      this.hideUploadPopup();

    }, error => {
      enf.disableSubmit = false;
      this.hideUploadPopup();
      console.error('Error in submit or upload:', error);
    });
  }

  postSubmitCleanup(enf: any, conversationHistory: string) {
    enf.isSubmitted = true;
    enf.comments = '';
    enf.mde_actionitem = enf.mde_actionitem || {};
    enf.mde_actionitem.mde_actionitemcommenthistory = conversationHistory;
    this.fetchEnforcementDetails();
  }

  onListItemClick(selectedItemIdx: number) {
    this.enforcementList.forEach((v: any, i: number) => {
      v.isActive = i === selectedItemIdx;
    });

    if (this.enforcementList[selectedItemIdx].link !== '') {
      const path = 'executor-dashboard/' + this.enforcementList[selectedItemIdx].link;
      this.router.navigate([path]);
    }
  }
  toggleDocuments(enf: any): void {
    enf.showDocuments = !enf.showDocuments;

    // If we are opening the section, and documents haven't been fetched, fetch them.
    if (enf.showDocuments && enf.fetchedDocuments.length === 0) {
      this.fetchEnforcementDocuments(enf);
    }
  }

  // 💡 NEW METHOD: Fetch documents for a single enforcement item
  fetchEnforcementDocuments(enf: any): void {
    // Clear any old documents immediately to prevent flickering
    enf.fetchedDocuments = [];

    const enforcementId = enf.mde_enforcementid || enf.enforcementId || enf.id;

    if (!enforcementId) {
      console.warn('Enforcement ID is missing for document fetch.');
      return;
    }

    this.enforcementService.fetchAppealDocuments(enforcementId).pipe(
      switchMap((res: any) => {
        const encrypted = res?.body?.data ?? res?.data ?? null;
        if (!encrypted) return of([]);
        const decrypted = this.commonService.decryptUsingAES256(encrypted);
        return of(Array.isArray(decrypted) ? decrypted : [decrypted]);
      })
    ).subscribe({
      next: (documents: any[]) => {
        // Add 'isExisting' flag to each document to mark as from backend
        enf.fetchedDocuments = documents.map(doc => ({
          ...doc,
          isExisting: true
        }));
      },
      error: (err) => {
        console.error('Error fetching enforcement documents', err);
        enf.fetchedDocuments = [];
      }
    });
  }
  // When multiple files selected, append them
  onEnforcementFileSelected(event: any, enforcementIndex: number) {
    const selectedFiles: FileList = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const enforcement = this.enforcementDetail[enforcementIndex];

    if (!enforcement.fileNames) enforcement.fileNames = [];
    if (!enforcement.attachedFiles) enforcement.attachedFiles = [];

    const MAX_NEW_FILES = 10;
    const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB

    const filesArr = Array.from(selectedFiles);

    // Count only new files (assuming enforcement.attachedFiles only contains new files)
    const currentNewFilesCount = enforcement.attachedFiles.length;

    // Check max files count including new selections
    if (currentNewFilesCount + filesArr.length > MAX_NEW_FILES) {
      event.target.value = '';
      return !this.isArabic ? alert('You can only attach up to 10 new files.') : alert('يمكنك إرفاق ما يصل إلى 10 ملفات جديدة فقط.');
    }

    // Calculate total size of newly selected files ONLY
    const newFilesTotalSize = filesArr.reduce((acc, f) => acc + f.size, 0);

    if (newFilesTotalSize > MAX_TOTAL_SIZE) {
      event.target.value = '';
      return !this.isArabic ? alert('Total size of newly selected files exceeds the 20 MB limit. Please select fewer or smaller files.') : alert('يجب ألا يتجاوز الحجم الإجمالي لجميع الملفات المضافة حديثًا 20 ميجابايت.');
    }

    for (let i = 0; i < filesArr.length; i++) {
      const file = filesArr[i];
      enforcement.fileNames.push(file.name);
      enforcement.attachedFiles.push(file);
    }

    // Clear the input so same file can be selected again if needed
    event.target.value = '';
  }

  // Replace single file at enforcementIndex and fileIndex
  onEnforcementSingleFileReplace(event: Event, enforcementIndex: number, fileIndex: number) {

    const inputEl = event.target as HTMLInputElement;

    if (!inputEl.files || inputEl.files.length === 0) {
      console.log('[onEnforcementSingleFileReplace] No files selected');
      return;
    }

    const file = inputEl.files[0];

    const enforcement = this.enforcementDetail[enforcementIndex];

    enforcement.fileNames[fileIndex] = file.name;
    enforcement.attachedFiles[fileIndex] = file;

    // Reset input so same file can be reselected again
    inputEl.value = '';

    enforcement.fileNames = [...enforcement.fileNames];
    enforcement.attachedFiles = [...enforcement.attachedFiles];

  }

  // Trigger hidden input file click to replace a specific file
  private isTriggeringFileInput = false;

  triggerFileInputForIndex(enforcementIndex: number, fileIndex: number) {
    if (this.isTriggeringFileInput) {
      console.log('Already triggering file input, skipping to prevent duplicate open');
      return;
    }
    this.isTriggeringFileInput = true;

    const inputId = `fileInput_${enforcementIndex}_${fileIndex}`;
    const inputElem = document.getElementById(inputId) as HTMLElement;
    if (inputElem) {
      inputElem.click();
    }
    // Reset the flag after a short delay to allow new triggers later
    setTimeout(() => {
      this.isTriggeringFileInput = false;
    }, 500); // 500 ms delay, adjust as needed
  }
  // Show popup and reset progress
  showUploadPopup() {
    const overlay = document.querySelector('.upload-overlay') as HTMLElement;

    const popup = document.getElementById('uploadPopup');
    const progressBar = document.getElementById('uploadProgressBar') as HTMLProgressElement;
    const progressPercent = document.getElementById('progressPercent');

    if (popup && progressBar && progressPercent) {
      popup.style.display = 'block';
      progressBar.value = 0;
      progressPercent.textContent = '0%';
    }
    if (overlay) overlay.style.display = 'block';

  }

  // Update progress
  updateUploadProgress(percent: number) {
    // console.log('updateUploadProgress called with percent:', percent);  // <-- DEBUG 

    const progressBar = document.getElementById('uploadProgressBar') as HTMLProgressElement;
    const progressPercent = document.getElementById('progressPercent');


    if (progressBar && progressPercent) {
      progressBar.value = percent;
      progressPercent.textContent = `${percent}%`;
      this.cd.detectChanges();

    }
  }

  // Hide popup
  hideUploadPopup() {
    const popup = document.getElementById('uploadPopup');
    if (popup) {
      popup.style.display = 'none';
    }
    const overlay = document.querySelector('.upload-overlay') as HTMLElement;
    if (overlay) overlay.style.display = 'none';
  }
}
